import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import AvatarUser from '../../components/AvatarUser';
import { Modal } from '../../components/Modal';
import EmojiPicker, { EmojiClickData, Theme } from 'emoji-picker-react';
import { api } from '../../AxiosConfig';
import { useAppContext } from '../../contexts/AppContext';
import { SettingsRow, SettingsSection } from '../configuracoes/estabelecimento/Form/SettingsGroup';
import MessageFormImage from './MessageFormImage';
import { Smile, Users } from 'lucide-react';

interface MessageFormProps {
  uid?: string;
  photoURL?: string;
  displayName?: string;
  pushOnly?: boolean;
  pushEmailOnly?: boolean;
  cancel: () => void;
}

const MAX_MESSAGE_LENGTH = 500;

type PopoverPos = { top: number; left: number; placement: 'top' | 'bottom' };

const MessageForm: React.FC<MessageFormProps> = ({
                                                   uid,
                                                   photoURL,
                                                   displayName,
                                                   pushOnly = false,
                                                   pushEmailOnly = false,
                                                   cancel,
                                                 }) => {
  const [message, setMessage] = useState('');
  const [sendWhatsApp, setSendWhatsApp] = useState(!pushOnly && !pushEmailOnly);
  const [sendPush, setSendPush] = useState(true);
  const [sendEmail, setSendEmail] = useState(false);
  const [loading, setLoading] = useState(false);
  const [imagesIds, setImagesIds] = useState<string[]>([]);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [confirmAll, setConfirmAll] = useState(false);

  const { errorSnackbar, successSnackbar, theme } = useAppContext();
  const emojiTheme = theme === 'dark' ? Theme.DARK : Theme.LIGHT;

  const emojiBtnRef = useRef<HTMLButtonElement | null>(null);
  const pickerRef = useRef<HTMLDivElement | null>(null);

  const [popoverPos, setPopoverPos] = useState<PopoverPos>({
    top: 0,
    left: 0,
    placement: 'bottom',
  });

  const isMobile = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia?.('(max-width: 640px)')?.matches ?? false;
  }, []);

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    setMessage((prev) => prev + emojiData.emoji);
    // não fecha automaticamente: melhor pra enviar vários emojis
  };

  const effectiveSendWhatsApp = pushOnly || pushEmailOnly ? false : sendWhatsApp;
  const effectiveSendPush = pushOnly ? true : sendPush;
  const effectiveSendEmail = pushOnly ? false : sendEmail;
  const isSubmitDisabled =
    loading ||
    uploadingImage ||
    !message.trim() ||
    (!effectiveSendWhatsApp && !effectiveSendPush && !effectiveSendEmail);

  useEffect(() => {
    if (!pushOnly && !pushEmailOnly) return;
    setSendWhatsApp(false);
    if (pushOnly) {
      setSendPush(true);
      setSendEmail(false);
    }
  }, [pushOnly, pushEmailOnly]);

  const computePopoverPosition = () => {
    const btn = emojiBtnRef.current;
    if (!btn) return;

    const rect = btn.getBoundingClientRect();

    // tamanho aproximado do emoji-picker (pode variar, mas funciona bem)
    const POPOVER_W = 360;
    const POPOVER_H = 420;
    const GAP = 8;

    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // tenta alinhar à direita do botão, mas sem estourar a tela
    let left = rect.right - POPOVER_W;
    left = Math.max(8, Math.min(left, vw - POPOVER_W - 8));

    // decide se abre pra baixo ou pra cima
    const spaceBelow = vh - rect.bottom;
    const spaceAbove = rect.top;

    const openBottom = spaceBelow >= POPOVER_H + GAP || spaceBelow >= spaceAbove;

    const top = openBottom
        ? rect.bottom + GAP
        : Math.max(8, rect.top - POPOVER_H - GAP);

    setPopoverPos({ top, left, placement: openBottom ? 'bottom' : 'top' });
  };

  const toggleEmojiPicker = () => {
    setShowEmojiPicker((prev) => {
      const next = !prev;
      if (next && !isMobile) {
        // calcula posição ANTES de renderizar o portal
        setTimeout(() => computePopoverPosition(), 0);
      }
      return next;
    });
  };

  // Fecha ao clicar fora + ESC, e reposiciona em scroll/resize (desktop)
  useEffect(() => {
    if (!showEmojiPicker) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowEmojiPicker(false);
    };

    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (emojiBtnRef.current?.contains(target)) return;
      if (pickerRef.current?.contains(target)) return;
      setShowEmojiPicker(false);
    };

    const onReflow = () => {
      if (!isMobile) computePopoverPosition();
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown, { passive: true });

    window.addEventListener('resize', onReflow);
    window.addEventListener('scroll', onReflow, true);

    // trava scroll no mobile quando overlay aberto
    const prevOverflow = document.body.style.overflow;
    if (isMobile) document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);

      window.removeEventListener('resize', onReflow);
      window.removeEventListener('scroll', onReflow, true);

      if (isMobile) document.body.style.overflow = prevOverflow;
    };
  }, [showEmojiPicker, isMobile]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (uploadingImage) {
      errorSnackbar('Aguarde o upload da imagem concluir para enviar.');
      return;
    }

    if (!message.trim() || (!effectiveSendWhatsApp && !effectiveSendPush && !effectiveSendEmail)) {
      errorSnackbar('Digite uma mensagem e selecione pelo menos um canal de envio.');
      return;
    }

    // Envio em massa nao tem como ser desfeito: exige confirmacao explicita.
    if (!uid) {
      setConfirmAll(true);
      return;
    }

    await sendMessage();
  };

  const sendMessage = async () => {
    setConfirmAll(false);
    setLoading(true);

    const messageData = {
      uid: uid,
      all: !uid,
      message: message.trim(),
      sendWhatsApp: effectiveSendWhatsApp,
      sendPush: effectiveSendPush,
      sendEmail: effectiveSendEmail,
      imageId: imagesIds[0],
    };

    try {
      const { data } = await api.post('/message/send', messageData);

      const whatsappOk = data?.channels?.whatsapp?.ok;
      const pushOk = data?.channels?.push?.ok;

      if (data?.channels) {
        const parts: string[] = [];
        if (effectiveSendWhatsApp) parts.push(`WhatsApp: ${whatsappOk ? '✅' : '❌'}`);
        if (effectiveSendPush) parts.push(`Push: ${pushOk ? '✅' : '❌'}`);
        if (effectiveSendEmail) {
          const emailOk = data?.channels?.email?.ok;
          parts.push(`Email: ${emailOk ? '✅' : '❌'}`);
        }
        successSnackbar(`Mensagem enviada. ${parts.join(' • ')}`);
      } else {
        successSnackbar('Mensagem enviada com sucesso!');
      }

      cancel();
    } catch (err: any) {
      const msg =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          'Não foi possível enviar a mensagem. Tente novamente.';
      errorSnackbar(msg);
    } finally {
      setLoading(false);
    }
  };

  // Portal do picker (desktop)
  const desktopPickerPortal =
      showEmojiPicker && !isMobile && typeof document !== 'undefined'
          ? createPortal(
              <div
                  ref={pickerRef}
                  className="fixed z-[9999] shadow-lg border border-base-200 rounded-xl overflow-hidden bg-base-100"
                  style={{
                    top: popoverPos.top,
                    left: popoverPos.left,
                    width: 360,
                    maxWidth: 'calc(100vw - 16px)',
                  }}
              >
                <EmojiPicker onEmojiClick={handleEmojiClick} lazyLoadEmojis skinTonesDisabled theme={emojiTheme} />
                <div className="p-2 border-t border-base-200 flex justify-end">
                  <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => setShowEmojiPicker(false)}
                  >
                    Fechar
                  </button>
                </div>
              </div>,
              document.body,
          )
          : null;

  return (
      <>
        <form onSubmit={handleSubmit} className="estab-settings space-y-6">
          <SettingsSection title="Para">
            <div className="flex min-h-[3.25rem] items-center gap-3 px-4 py-2">
              {uid ? (
                <>
                  <AvatarUser photoURL={photoURL} name={displayName || 'Cliente'} size="h-9 w-9 !text-xs" />
                  <span className="min-w-0 truncate text-sm font-medium text-base-content">{displayName}</span>
                </>
              ) : (
                <>
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-base-content/10 text-base-content/60">
                    <Users className="text-sm" aria-hidden />
                  </span>
                  <span className="text-sm font-medium text-base-content">Todos os clientes do estabelecimento</span>
                </>
              )}
            </div>
          </SettingsSection>

          <SettingsSection
            title="Mensagem"
            footer={`${message.length}/${MAX_MESSAGE_LENGTH} caracteres`}
          >
            <div className="relative p-2">
              <textarea
                  id="message"
                  aria-label="Mensagem"
                  value={message}
                  maxLength={MAX_MESSAGE_LENGTH}
                  onChange={(e) => setMessage(e.target.value)}
                  className="textarea estab-field !ml-0 !min-h-[8rem] !w-full !bg-transparent pr-10 focus:!bg-transparent"
                  placeholder={uid ? 'Escreva a mensagem para o cliente...' : 'Escreva a mensagem para os clientes...'}
                  rows={4}
                  required
              />

              {/* Botão emoji */}
              <button
                  ref={emojiBtnRef}
                  type="button"
                  onClick={toggleEmojiPicker}
                  className="absolute bottom-3 right-3 btn btn-ghost btn-sm btn-circle text-base-content/60"
                  aria-label="Abrir emojis"
              >
                <Smile aria-hidden />
              </button>
            </div>
          </SettingsSection>

          <SettingsSection title="Imagem">
            <MessageFormImage
                value={imagesIds}
                setValue={setImagesIds}
                disabled={loading}
                onUploadingChange={setUploadingImage}
            />
          </SettingsSection>

          {/* Seleção de canais */}
          <SettingsSection
            title={pushOnly ? 'Canal de envio' : 'Canais de envio'}
            footer={
              !effectiveSendWhatsApp && !effectiveSendPush && !effectiveSendEmail ? (
                <span className="text-error">Selecione pelo menos um canal de envio.</span>
              ) : undefined
            }
          >
            {!pushOnly && !pushEmailOnly && (
              <SettingsRow label="WhatsApp" htmlFor="whatsapp" description="Mensagem pelo WhatsApp do cliente." inline>
                <input
                    id="whatsapp"
                    name="whatsapp"
                    type="checkbox"
                    checked={sendWhatsApp}
                    onChange={(e) => setSendWhatsApp(e.target.checked)}
                    className="toggle toggle-success"
                />
              </SettingsRow>
            )}

            <SettingsRow label="Push" htmlFor="push" description="Notificação pelo aplicativo." inline>
              <input
                  id="push"
                  name="push"
                  type="checkbox"
                  checked={effectiveSendPush}
                  onChange={(e) => setSendPush(e.target.checked)}
                  disabled={pushOnly}
                  className="toggle toggle-success"
              />
            </SettingsRow>

            {!pushOnly && (
              <SettingsRow label="E-mail" htmlFor="email" description="Mesma mensagem no e-mail do cliente." inline>
                <input
                  id="email"
                  name="email"
                  type="checkbox"
                  checked={sendEmail}
                  onChange={(e) => setSendEmail(e.target.checked)}
                  className="toggle toggle-success"
                />
              </SettingsRow>
            )}
          </SettingsSection>

          {/* Ações */}
          <div className="estab-savebar gap-2">
            <button
              type="button"
              onClick={cancel}
              className="btn btn-ghost flex-1 sm:flex-none sm:min-w-[8rem]"
              disabled={loading}
            >
              Cancelar
            </button>

            <button
              type="submit"
              className="btn btn-primary flex-1 sm:flex-none sm:min-w-[11rem]"
              disabled={isSubmitDisabled}
            >
              {loading && <span className="loading loading-spinner loading-sm"></span>}
              {pushOnly ? 'Enviar push' : 'Enviar mensagem'}
            </button>
          </div>
        </form>

        {confirmAll && (
          <Modal
            onClose={() => setConfirmAll(false)}
            title="Enviar para todos os clientes?"
            desktopClassName="sm:!max-w-md"
          >
            <p className="text-sm text-base-content/70">
              A mensagem será enviada a todos os clientes do estabelecimento, sem considerar os filtros da lista. Esse
              envio não pode ser desfeito.
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" className="btn btn-ghost" onClick={() => setConfirmAll(false)}>
                Cancelar
              </button>
              <button type="button" className="btn btn-primary" onClick={() => void sendMessage()}>
                Enviar para todos
              </button>
            </div>
          </Modal>
        )}

        {/* Portal desktop */}
        {desktopPickerPortal}

        {/* Mobile bottom sheet */}
        {showEmojiPicker && isMobile && (
            <div className="fixed inset-0 z-[9999] bg-black/40 flex items-end">
              <div
                  ref={pickerRef}
                  className="w-full bg-base-100 rounded-t-2xl border border-base-200 shadow-xl max-h-[75vh] overflow-hidden"
              >
                <div className="flex items-center justify-between px-4 py-3 border-b border-base-200">
                  <div className="font-medium text-sm">Escolher emoji</div>
                  <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => setShowEmojiPicker(false)}
                  >
                    Fechar
                  </button>
                </div>

                <div className="overflow-auto max-h-[calc(75vh-56px)]">
                  <EmojiPicker onEmojiClick={handleEmojiClick} lazyLoadEmojis skinTonesDisabled theme={emojiTheme} />
                </div>
              </div>
            </div>
        )}
      </>
  );
};

export default MessageForm;
